import * as React from 'react'
import * as LabelPrimitive from '@radix-ui/react-label'
import { cn } from '@/lib/utils'

export const inputClass =
  'flex h-9 w-full rounded-lg border border-line bg-surface px-3 text-[13px] text-ink placeholder:text-muted/70 focus-visible:outline-2 focus-visible:outline-accent focus-visible:outline-offset-0 disabled:opacity-60 aria-[invalid=true]:border-red'

export const Input = React.forwardRef<HTMLInputElement, React.InputHTMLAttributes<HTMLInputElement>>(({ className, ...props }, ref) => (
  <input ref={ref} className={cn(inputClass, className)} {...props} />
))
Input.displayName = 'Input'

export const Textarea = React.forwardRef<HTMLTextAreaElement, React.TextareaHTMLAttributes<HTMLTextAreaElement>>(({ className, ...props }, ref) => (
  <textarea ref={ref} className={cn(inputClass, 'h-auto min-h-[80px] py-2 leading-relaxed', className)} {...props} />
))
Textarea.displayName = 'Textarea'

export const Label = React.forwardRef<React.ElementRef<typeof LabelPrimitive.Root>, React.ComponentPropsWithoutRef<typeof LabelPrimitive.Root>>(({ className, ...props }, ref) => (
  <LabelPrimitive.Root ref={ref} className={cn('text-[12px] font-semibold text-ink', className)} {...props} />
))
Label.displayName = 'Label'

/**
 * A labelled form field with help text and an error. The error names the rule
 * broken and how to fix it; the input keeps its value (screen spec §2).
 */
export function Field({
  label,
  htmlFor,
  help,
  error,
  required,
  children,
  className,
  optional,
}: {
  label: React.ReactNode
  htmlFor?: string
  help?: React.ReactNode
  error?: string | null
  required?: boolean
  optional?: boolean
  children: React.ReactNode
  className?: string
}) {
  const helpId = htmlFor ? `${htmlFor}-help` : undefined
  const errId = htmlFor ? `${htmlFor}-error` : undefined
  return (
    <div className={cn('grid gap-1.5', className)}>
      <Label htmlFor={htmlFor}>
        {label}
        {required && <span className="text-red"> *</span>}
        {optional && <span className="font-normal text-muted"> (optional)</span>}
      </Label>
      {children}
      {help && !error && (
        <p id={helpId} className="text-[11px] leading-snug text-muted">
          {help}
        </p>
      )}
      {error && (
        <p id={errId} role="alert" className="text-[11px] font-medium leading-snug text-red">
          {error}
        </p>
      )}
    </div>
  )
}
